// Image preloader utility for onboarding tour assets
// Preloads all images and videos used across tour slides to improve performance

import { ALL_TOUR_IMAGES, ALL_TOUR_VIDEOS } from './asset_constants'

export interface PreloadResult {
  loaded: string[]
  failed: string[]
  total: number
}

class AssetPreloader {
  private loadedAssets: Set<string> = new Set()
  private failedAssets: Set<string> = new Set()

  private preloadImage(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(src)
      img.onerror = () => reject(src)
      img.src = src
    })
  }

  private preloadVideo(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.onloadedmetadata = () => resolve(src)
      video.onerror = () => reject(src)
      video.preload = 'metadata'
      video.src = src
    })
  }

  async preloadAssets(): Promise<PreloadResult> {
    const imagePromises = ALL_TOUR_IMAGES.map(src => 
      this.preloadImage(src)
        .then(src => {
          this.loadedAssets.add(src)
          return src
        })
        .catch(src => {
          this.failedAssets.add(src)
          throw src
        })
    )

    const videoPromises = ALL_TOUR_VIDEOS.map(src => 
      this.preloadVideo(src)
        .then(src => {
          this.loadedAssets.add(src)
          return src
        })
        .catch(src => {
          this.failedAssets.add(src)
          throw src
        })
    )

    const allPromises = [...imagePromises, ...videoPromises]
    const results = await Promise.allSettled(allPromises)

    const loaded = results
      .filter(result => result.status === 'fulfilled')
      .map(result => (result as PromiseFulfilledResult<string>).value)

    const failed = results
      .filter(result => result.status === 'rejected')
      .map(result => (result as PromiseRejectedResult).reason)

    return {
      loaded,
      failed,
      total: allPromises.length
    }
  }

  getPreloadedAssets(): string[] {
    return Array.from(this.loadedAssets)
  }

  getFailedAssets(): string[] {
    return Array.from(this.failedAssets)
  }

  isAssetPreloaded(src: string): boolean {
    return this.loadedAssets.has(src)
  }
}

export default AssetPreloader